import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  unwrapTrpcData,
} from '@org/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from './products-list';

type ProductRow = RouterOutputs['products']['list'][number];

type ProductSelectProps = {
  value: string | undefined;
  onChange: (productId: string) => void;
  disabled?: boolean;
};

/**
 * Picker of active products, used by the student "Access" dialog to grant a
 * product by hand. Inactive products are hidden.
 */
export function ProductSelect({ value, onChange, disabled }: ProductSelectProps) {
  const { t, i18n } = useTranslation('admin');
  const { data } = trpc.products.list.useQuery();

  const options = useMemo(() => {
    const products = unwrapTrpcData<ProductRow[]>(data as ProductRow[]) ?? [];
    return products.filter((product) => product.isActive);
  }, [data]);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full text-left">
        <SelectValue placeholder={t('products.select.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        {options.map((product) => (
          <SelectItem key={product.productId} value={product.productId}>
            {product.name}
            {product.journeyName ? ` — ${product.journeyName}` : ''}
            {' · '}
            {product.priceCents === 0
              ? t('products.list.free')
              : formatPrice(product.priceCents, product.currency, i18n.language)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
